import { IDisposable, EventEmitter, EventSubscription } from '@sm/max-common'
import { cache } from '@platform/cache'
import { set, get } from '../utils/object'
import { _default } from './ctx'
import { GLOBAL_VARIABLES_CACHE_KEY, GLOBAL_VARIABLES_EVENT } from '../utils/constant'
import { deepClone } from '../utils'

export interface DataAPI extends IDisposable {
  /**
   * Clear data. [SUNMI Doc](https://developer.sunmi.com/docs/%{locale}%/cfacqeghjk513/mmceghjk502)
   * @param key Key value
   * @param [level="page"] Mount area
   */
  clear(key: string): void
  /**
   * Obtain the data in the current level view. [SUNMI Doc](https://developer.sunmi.com/docs/%{locale}%/cfacqeghjk513/mmxeghjk491)
   * @param key Key value
   * @param [level="page"] Mount area
   */
  get<T = any>(key: string): T
  /**
   * Set the data under the current level view. [SUNMI Doc](https://developer.sunmi.com/docs/%{locale}%/cfacqeghjk513/mmaeghjk480)
   * @param key Key value
   * @param value Value content, if it is null or '', delete the Item. Object or []Object must be a serializable Object
   * @param [level="page"] Mount area
   */
  set<T>(key: string, value: T): void
  onChange: EventSubscription<any>

  // inner
  merge(val: any): void
}

export interface IDataStore extends DataAPI {
  readonly store: Record<string, any>
}

const partUpdate = /^([a-zA-Z0-9\_\$]*)\[([0-9]*)\]*/

export function DataStore(pageCode: string, isGlobal = false): IDataStore {
  let store: Record<string, any> = {}
  const onChangeEmitter = new EventEmitter<any>()

  const storeSet = (key: string, value: any, isNotifyGlobalListener = true) => {
    if (isGlobal) {
      const _cache = JSON.parse(cache.get(GLOBAL_VARIABLES_CACHE_KEY) || null)
      store = Object.assign(store, _cache)
    }
    set(store, key, value)
    const res = partUpdate.exec(key)
    if (__platform === 'android' && isGlobal && isNotifyGlobalListener) {
      TinyAPI.cache.set(GLOBAL_VARIABLES_CACHE_KEY, JSON.stringify(store))
      // app worker 中 getInstanceId 为 undefined
      const instanceId = typeof TinyAPI.navigation.getInstanceId === 'function'
        ? TinyAPI.navigation.getInstanceId()
        : 'APP_WORKER'
      TinyAPI.event.send(GLOBAL_VARIABLES_EVENT, JSON.stringify({
        key,
        value,
        // 事件发送方不需要再执行事件的回调, 通过 instanceId 来区分
        instanceId,
      }))
    } else if (__platform !== 'android' && isGlobal && isNotifyGlobalListener) {
      cache.set(GLOBAL_VARIABLES_CACHE_KEY, JSON.stringify(store))
    }

    if (__platform === 'preview' && PreviewAPI?.projectType === 'app' && isGlobal && isNotifyGlobalListener) {
      let instanceId = 'APP_WORKER'
      try {
        instanceId = _default.max.router.getCurrent()?.instanceId
      } catch (error) {
        console.log('PreviewAPI.event.send.GLOBAL_VARIABLES_EVENT', error)
      }
      PreviewAPI.event.send.call(null, GLOBAL_VARIABLES_EVENT, JSON.stringify({ key, value, instanceId }))
    }

    if (res) {
      const key = res[1]
      const index = parseInt(res[2])
      onChangeEmitter.emit({ key, value, index })
    } else {
      onChangeEmitter.emit({ key, value })
    }
  }

  const storeGet = (key: string, scope?: string) => {
    if (scope === 'root') {
      return _default.max.data.get(key)
    }
    return get(store, key)
  }

  const storeMerge = (obj: any) => {
    Object.assign(store, deepClone(obj))
  }


  const storeClear = (key: string) => {
    delete store[key]
  }

  return {
    set: storeSet,
    get: storeGet,
    merge: storeMerge,
    clear: storeClear,
    get onChange() {
      return onChangeEmitter.event
    },
    get store() {
      return {
        ...store,
      }
    },
    dispose() {
      onChangeEmitter.dispose()
    }
  }
}
